import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from "@/components/ui/button";
import { Copy, ArrowLeft, ExternalLink } from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vs } from 'react-syntax-highlighter/dist/esm/styles/prism';

const GoogleDocsIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 81 111.372"
    className="w-4 h-4 shrink-0"
  >
    <path fill="#4285f4" d="M50.623 0H7.591A7.786 7.786 0 0 0 0 7.6v96.182a7.786 7.786 0 0 0 7.6 7.6h65.8a7.786 7.786 0 0 0 7.6-7.6v-73.4L63.284 17.716Z" />
    <path fill="#f1f1f1" d="M20.247 80.996h40.5v-5.061h-40.5v5.061Zm0 10.126h30.372v-5.061H20.247Zm0-35.437v5.061h40.5v-5.062Zm0 15.186h40.5V65.81h-40.5v5.061Z" />
    <path fill="#a1c2fa" d="M50.623 0v22.781a7.782 7.782 0 0 0 7.591 7.591h22.781Z" />
  </svg>
);

const NotionIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 64 64" 
    className="w-4 h-4 shrink-0"
  >
    <path 
      fill="currentColor" 
      fillRule="evenodd" 
      d="m5.2,47.56s8,10.37,8.48,10.83c1.16,1.11,2.73,1.69,4.33,1.6,8.37-.42,27.54-1.38,35.57-1.78,3.11-.16,5.55-2.72,5.56-5.83l.1-35.5c0-1.99-1.03-3.83-2.72-4.87t0,0c-2.99-1.84-8.91-5.49-10.7-6.68-1.46-.97-3.2-1.43-4.96-1.32-5.96.38-23.45,1.51-30.85,1.98-2.96.19-5.24,2.62-5.24,5.54v34.78c0,.45.15.89.43,1.24h0Z"
    />
  </svg>
);

interface BlogContentProps {
  content: string;
  onReset: () => void;
  title?: string;
}

const extractMetaDescription = (content: string) => {
  const metaMatch = content.match(/<!--\s*(.*?)\s*-->/);
  return metaMatch ? metaMatch[1].trim() : null;
};

const processContent = (content: string) => {
  // First extract meta description if present
  const metaDescription = extractMetaDescription(content);
  
  // Remove meta description from content
  let processedContent = content.replace(/<!--.*?-->/, '').trim();
  
  return processedContent;
};

export default function BlogContent({ content, onReset, title }: BlogContentProps) {
  const { toast } = useToast();
  const metaDescription = extractMetaDescription(content);
  const processedContent = processContent(content);

  const handleExternalOpen = async (service: 'notion' | 'gdocs') => {
    try {
      await navigator.clipboard.writeText(processedContent);
      
      const url = service === 'notion' ? 'https://notion.new' : 'https://docs.new';
      window.open(url, '_blank');
      
      toast({
        title: "Content copied!",
        description: `Content copied to clipboard. Opening ${service === 'notion' ? 'Notion' : 'Google Docs'}...`,
      });
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Please try copying the content manually before opening the editor.",
        variant: "destructive",
      });
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(processedContent);
      toast({
        title: "Content copied!",
        description: "The blog post has been copied to your clipboard.",
      });
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Please try again or copy manually.",
        variant: "destructive",
      });
    }
  };

  return (
    <article className="bg-white rounded-xl shadow-lg p-8 relative">
      
      {/* Mobile Action Box */}
      <div className="md:hidden">
        <div className="bg-white border-b pb-4 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onReset}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <h1 className="text-xl font-bold truncate">{title}</h1>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center justify-center gap-2"
              onClick={handleCopy}
            >
              <Copy className="w-4 h-4" />
              <span className="hidden sm:inline">Copy</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center justify-center gap-2"
              onClick={() => handleExternalOpen('notion')}
            >
              <NotionIcon />
              <span className="hidden sm:inline">Notion</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center justify-center gap-2"
              onClick={() => handleExternalOpen('gdocs')}
            >
              <GoogleDocsIcon />
              <span className="hidden sm:inline">GDocs</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden md:flex items-center justify-between gap-4 mb-6 not-prose">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">{title}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={handleCopy}
          >
            <Copy className="w-4 h-4" />
            Copy Content
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={() => handleExternalOpen('notion')}
          >
            <NotionIcon />
            Notion
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={() => handleExternalOpen('gdocs')}
          >
            <GoogleDocsIcon />
            Google Docs
          </Button>
        </div>
      </div>

      {metaDescription && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-8">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Meta Description</h3>
          <p className="text-gray-600 text-sm">{metaDescription}</p>
        </div>
      )}

      <div className={cn(
        "prose prose-lg max-w-none",
        "prose-headings:font-heading prose-headings:font-bold",
        "prose-h1:text-4xl prose-h1:mb-6 prose-h1:mt-0",
        "prose-h2:text-3xl prose-h2:mb-4 prose-h2:mt-8",
        "prose-h3:text-2xl prose-h3:mb-3 prose-h3:mt-6",
        "prose-p:my-4 prose-p:leading-relaxed",
        "prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6",
        "prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-6",
        "prose-li:mb-2",
        "prose-blockquote:border-l-4 prose-blockquote:border-dogblue prose-blockquote:pl-4 prose-blockquote:my-6 prose-blockquote:italic prose-blockquote:bg-gray-50 prose-blockquote:py-2 prose-blockquote:pr-4 prose-blockquote:rounded-r",
        "prose-code:bg-gray-100 prose-code:rounded prose-code:px-1 prose-code:py-0.5 prose-code:text-sm prose-code:font-mono",
        "prose-strong:font-bold prose-strong:text-gray-900",
        "prose-a:text-dogblue prose-a:no-underline hover:prose-a:underline"
      )}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({node, children, ...props}) => (
              <h1 className="text-4xl font-bold !mt-0 !mb-6 flex items-baseline gap-3" {...props}>
                <span className="text-sm font-mono text-dogblue uppercase shrink-0">H1:</span>
                <span className="flex-1">{children}</span>
              </h1>
            ),
            h2: ({node, children, ...props}) => (
              <h2 className="text-3xl font-semibold !mt-8 !mb-4 flex items-baseline gap-3" {...props}>
                <span className="text-sm font-mono text-dogblue uppercase shrink-0">H2:</span>
                <span className="flex-1">{children}</span>
              </h2>
            ),
            h3: ({node, children, ...props}) => (
              <h3 className="text-2xl font-semibold !mt-6 !mb-3 flex items-baseline gap-3" {...props}>
                <span className="text-sm font-mono text-dogblue uppercase shrink-0">H3:</span>
                <span className="flex-1">{children}</span>
              </h3>
            ),
            p: ({node, children, ...props}) => (
              <p className="my-4 leading-relaxed" {...props}>{children}</p>
            ),
            code: ({node, inline, ...props}) => (
              inline ? (
                <code {...props} />
              ) : (
                <pre className="bg-gray-900 text-white p-4 rounded-lg overflow-x-auto">
                  <code {...props} />
                </pre>
              )
            ),
            table: ({node, children, ...props}) => (
              <div className="overflow-x-auto my-8">
                <table className="min-w-full border-collapse border border-gray-200 rounded-lg" {...props}>
                  {children}
                </table>
              </div>
            ),
            thead: ({node, children, ...props}) => (
              <thead className="bg-gray-50" {...props}>
                {children}
              </thead>
            ),
            tbody: ({node, children, ...props}) => (
              <tbody className="divide-y divide-gray-200 bg-white" {...props}>
                {children}
              </tbody>
            ),
            tr: ({node, children, ...props}) => (
              <tr className="hover:bg-gray-50 transition-colors" {...props}>
                {children}
              </tr>
            ),
            th: ({node, children, ...props}) => (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200" {...props}>
                {children}
              </th>
            ),
            td: ({node, children, ...props}) => (
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200 text-center" {...props}>
                {children}
              </td>
            ),
          }}
        >
          {processedContent}
        </ReactMarkdown>
      </div>

      <div className="!mt-8 !pt-6 border-t not-prose">
        <Button 
          onClick={onReset}
          variant="outline"
        >
          Generate Another Post
        </Button>
      </div>
    </article>
  );
}